import { Engine } from 'json-rules-engine';

// Java String.trim: strips the characters up to U+0020 only (a non-breaking
// space stays, unlike String.prototype.trim)
const javaTrim = (value: string): string =>
  // eslint-disable-next-line no-control-regex
  value.replace(/^[\u0000- ]+|[\u0000- ]+$/g, '');

// #128: the IN / NOT_IN list of a rule condition, as the server builds it
// (return-api RuleConditionMvelBuilder.listValues, #271): Java
// `value.split(",")` - trailing empty elements dropped, leading / inner empty
// elements kept -, each element trimmed; a list with no element at all keeps
// one empty element. "Damaged, Too big" -> ["Damaged", "Too big"].
export const ruleListValues = (value: unknown): string[] => {
  const elements = String(value ?? '').split(',');
  // Java drops the trailing EMPTY elements before the server trims
  while (elements.length > 0 && elements[elements.length - 1] === '') {
    elements.pop();
  }
  return elements.length > 0 ? elements.map(javaTrim) : [''];
};

// a finite number from a number or a numeric text, else undefined
export const ruleNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value !== 'string' || javaTrim(value) === '') return undefined;
  const n = Number(javaTrim(value));
  return Number.isFinite(n) ? n : undefined;
};

const listOf = (operand: unknown): string[] =>
  Array.isArray(operand)
    ? operand.map(element => String(element))
    : ruleListValues(operand);

// STRING list: strict text equality per element (MVEL compares quoted
// strings: "02" is not "2")
const inTextList = (factValue: unknown, operand: unknown): boolean =>
  factValue !== undefined &&
  factValue !== null &&
  listOf(operand).includes(String(factValue));

// NUMBER list: numeric equality per element (MVEL `price == 50.0`)
const inNumberList = (factValue: unknown, operand: unknown): boolean => {
  const fact = ruleNumber(factValue);
  return (
    fact !== undefined &&
    listOf(operand).some(element => ruleNumber(element) === fact)
  );
};

// #130: mirror return-api GatewayListMatch, preserving empty gateways.
const normalizedGateways = (value: unknown): string[] =>
  String(value)
    .split(',')
    .map(element => javaTrim(element).toLowerCase());

export const gatewayEquals = (
  factValue: unknown,
  operand: unknown
): boolean => {
  if (factValue == null || operand == null) return false;
  const gateways = normalizedGateways(factValue);
  const wanted = normalizedGateways(operand);
  return (
    gateways.includes(javaTrim(String(operand)).toLowerCase()) ||
    (gateways.length === wanted.length &&
      gateways.every((gateway, index) => gateway === wanted[index]))
  );
};

export const gatewayIn = (factValue: unknown, operand: unknown): boolean =>
  listOf(operand).some(value => gatewayEquals(factValue, value));

export const gatewayContains = (
  factValue: unknown,
  operand: unknown
): boolean =>
  factValue != null &&
  operand != null &&
  String(factValue).toLowerCase().includes(String(operand).toLowerCase());

export const gatewayOperator = (name: string): string =>
  (
    ({
      equal: 'gatewayEquals',
      notEqual: 'gatewayNotEquals',
      in: 'gatewayIn',
      notIn: 'gatewayNotIn',
      contains: 'gatewayContains',
      doesNotContain: 'gatewayNotContains',
    }) as Record<string, string>
  )[name] ?? name;

// The json-rules-engine operator of an IN / NOT_IN condition: the server
// compares a NUMBER list numerically, any other list as text.
export const listOperator = (name: string, valueType?: string): string => {
  if ((valueType ?? '').toUpperCase() !== 'NUMBER') return name;
  if (name === 'in') return 'numberIn';
  if (name === 'notIn') return 'numberNotIn';
  return name;
};

// #128: json-rules-engine's default `in` / `notIn` test
// `operand.indexOf(fact)`, a SUBSTRING test on the raw string value of a
// condition ("Too" was in "Damaged,Too big"). Overrides them on an engine
// with the server's list semantics (and adds their NUMBER variants, see
// listOperator); every other operator is unchanged.
export const registerListOperators = (engine: Engine): void => {
  engine.addOperator('gatewayEquals', gatewayEquals);
  engine.addOperator(
    'gatewayNotEquals',
    (fact, value) => !gatewayEquals(fact, value)
  );
  engine.addOperator('gatewayIn', gatewayIn);
  engine.addOperator('gatewayNotIn', (fact, value) => !gatewayIn(fact, value));
  engine.addOperator('in', inTextList);
  engine.addOperator(
    'notIn',
    (factValue: unknown, operand: unknown) => !inTextList(factValue, operand)
  );
  engine.addOperator('numberIn', inNumberList);
  engine.addOperator(
    'numberNotIn',
    (factValue: unknown, operand: unknown) => !inNumberList(factValue, operand)
  );
};

// MVEL `contains` as the server emits it for CONTAINS / NOT_CONTAINS
// (return-api RuleConditionMvelBuilder.buildPredicate): Java String.contains
// on a text (substring, case-sensitive), membership on a list. A fact that is
// neither is unknown -> false both ways (the portal never requires a photo or
// hides an item on a guess).
const containsOperand = (factValue: unknown, operand: unknown): boolean => {
  if (Array.isArray(factValue)) return factValue.includes(operand);
  if (typeof factValue === 'string') return factValue.includes(String(operand));
  return false;
};

// #133: json-rules-engine's default `contains` / `doesNotContain` only accept
// an ARRAY fact (always false on a text such as the reason name). Overrides
// them on an engine with the server semantics above; every other operator is
// unchanged.
export const registerTextOperators = (engine: Engine): void => {
  engine.addOperator('gatewayContains', gatewayContains);
  engine.addOperator(
    'gatewayNotContains',
    (fact, value) => !gatewayContains(fact, value)
  );
  engine.addOperator('contains', containsOperand);
  engine.addOperator(
    'doesNotContain',
    (factValue: unknown, operand: unknown) =>
      (Array.isArray(factValue) || typeof factValue === 'string') &&
      !containsOperand(factValue, operand)
  );
};
